import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Building2, 
  Shield, 
  Clock, 
  DollarSign, 
  Users, 
  FileText, 
  Wrench, 
  BarChart3, 
  Phone, 
  CheckCircle2,
  ArrowRight,
  Home,
  Calculator,
  ClipboardList,
  Headphones,
  Key,
  PiggyBank
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function CondoManagementPage() {
  const services = [
    {
      icon: Key,
      title: "Leasing & Tenant Placement",
      items: [
        "Unit leasing and marketing",
        "Lease drafting with condominium-specific compliance riders",
        "Application processing, credit checks, and background screening",
        "Condo board package preparation and submission",
        "Move-in and move-out coordination"
      ]
    },
    {
      icon: FileText,
      title: "Lease Administration & Compliance",
      items: [
        "Lease renewals and rent increases",
        "Lease enforcement",
        "Verification of renter's insurance policies",
        "Condo board alteration agreements",
        "Compliance with all building house rules and bylaws"
      ]
    },
    {
      icon: DollarSign,
      title: "Financial Management",
      items: [
        "Rent collection and online rent payments",
        "Establishment of owner trust accounts",
        "Payment of mortgage, property taxes, and common charges",
        "Monthly income and expense reporting",
        "Security deposit processing"
      ]
    },
    {
      icon: Wrench,
      title: "Maintenance & Building Coordination",
      items: [
        "24/7 call answering for tenant inquiries",
        "Emergency response services",
        "HVAC service contracts",
        "Vendor coordination and insurance verification",
        "Coordination with building managing agents"
      ]
    },
    {
      icon: BarChart3,
      title: "Reporting & Asset Management",
      items: [
        "Monthly financial reports",
        "Unit inspections and property evaluations",
        "Asset analysis reports with comparable sales",
        "Coordination of tax and legal services",
        "Strategic guidance for refinancing or resale"
      ]
    },
    {
      icon: Home,
      title: "Resale & Exit Services",
      items: [
        "Unit listing and marketing for resale",
        "Portfolio disposition planning",
        "Coordination with brokers, attorneys, and closing agents",
        "1031 exchange coordination",
        "Market analysis and pricing guidance"
      ]
    }
  ];

  const highlights = [
    {
      icon: Headphones,
      title: "24/7 Availability",
      description: "Round-the-clock response to tenant inquiries and emergencies"
    },
    {
      icon: ClipboardList,
      title: "Board Package Expertise",
      description: "Expert preparation and submission of condo board rental packages"
    },
    {
      icon: Calculator,
      title: "Pro Forma Analysis",
      description: "Detailed property analysis including financial modeling"
    },
    {
      icon: PiggyBank,
      title: "Optimize Performance",
      description: "Strategic pricing and operational efficiency to optimize your property's performance"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-[#1A1A2E] text-white py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A2E] to-[#2D2D4A]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-3 py-1.5 bg-[#0066FF] text-white text-xs font-bold uppercase tracking-wider rounded mb-4">
              Property Management
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white">
              Condo Unit Management
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-8">
              Expert condominium unit management for property owners in NYC and Miami. 
              End-to-end leasing, board approvals, compliance, reporting, and asset strategy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button className="btn-primary px-8 py-6 text-base rounded-lg group font-semibold">
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/properties">
                <Button variant="outline" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 px-8 py-6 text-base rounded-lg font-semibold">
                  View Properties
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Is Condo Unit Management */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-6">
                What Is Condo Unit Management?
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                <strong>Condo unit management</strong> is the management of individual condominium units on behalf of the owner when the unit is rented by a tenant.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                Unlike building-wide management—where a managing agent oversees common areas, staff, and building operations on behalf of the condominium association—condo unit management represents the interests of the individual owner.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Our property management company specializes in providing tailored services for condominium owners who need a reliable local manager. We handle rent collection, payment of common charges, enforcement of house rules, and ensure full compliance with local regulations.
              </p>
              <div className="flex items-center gap-2 text-[#0066FF] font-medium">
                <Shield className="w-5 h-5" />
                <span>Krieger Real Estate is a pioneer in this management niche</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {highlights.map((item, idx) => (
                <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <item.icon className="w-10 h-10 text-[#0066FF] mb-4" />
                  <h3 className="font-semibold text-[#1A1A2E] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600">{item.description}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Services List */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-4">
              Our Condo Unit Management Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A practical and affordable solution for owners of one or more condo units requiring professional management.
            </p>
          </div>

          <div className="bg-[#1A1A2E] rounded-2xl p-8 lg:p-12 text-white">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "24/7 availability to respond to tenant inquiries",
                "Online tracking of all maintenance issues",
                "Online automated ACH rent payment system",
                "Online tenant portal for payments and work orders",
                "Comprehensive marketing and leasing services",
                "Advanced research tools for rental pricing",
                "Application processing and credit screening",
                "Condo Board Package preparation and submission",
                "Turnover painting and repair coordination",
                "Tenant security deposit management",
                "Coordination with building staff",
                "HVAC service scheduling",
                "Emergency response capability",
                "Regular unit inspection service",
                "Monthly financial reports",
                "Coordination of tax and legal services",
                "Alterations Agreement submissions",
                "Insurance certificate coordination",
                "Tenant renters insurance policy online",
                "Lease renewal management",
                "Payment of mortgage and taxes"
              ].map((service, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-sm">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Services Grid */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-4">
              Comprehensive Management Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From acquisition to exit, we deliver a seamless experience that allows you to focus on strategy while we manage execution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <service.icon className="w-10 h-10 text-[#0066FF] mb-4" />
                <h3 className="text-lg font-semibold text-[#1A1A2E] mb-4">{service.title}</h3>
                <ul className="space-y-2">
                  {service.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Investing in Condo Units */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-6">
                A Flexible Portfolio Strategy
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Condominium unit ownership offers flexibility in terms of property type, location, and management approach. Owners may choose to:
              </p>
              <ul className="space-y-4 mb-6">
                {[
                  "Purchase a single condo unit as a long-term rental or pied-a-terre",
                  "Assemble a portfolio of units within the same building for operational efficiencies",
                  "Build a multi-building portfolio across neighborhoods",
                  "Diversify geographically by owning units in both NYC and Miami"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-600 leading-relaxed">
                Because each unit is deeded separately, owners retain the ability to sell units individually, reallocate capital through 1031 exchanges, and adjust holdings across neighborhoods over time.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-2xl p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6">Pro Forma Property Analysis</h3>
              <p className="text-white/90 mb-6">
                Before acquisition—and continuously throughout ownership—we conduct detailed pro forma analysis tailored specifically to condominium units.
              </p>
              <ul className="space-y-3">
                {[
                  "Common charges and assessment history",
                  "Unit-specific real estate taxes",
                  "Available tax abatements (421-a, J-51, etc.)",
                  "Building leasing policies and restrictions",
                  "Typical approval timelines and fees",
                  "Vacancy and turnover assumptions",
                  "Long-term appreciation scenarios"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-white/90 text-sm">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Krieger */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mb-4">
              Why Choose Krieger Real Estate?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Managing a condominium unit is administratively intensive and requires constant coordination. This is where most general property managers fall short—and where we excel.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: "Specialized Expertise",
                description: "We exclusively focus on condominium unit management in NYC and Miami, giving us unmatched knowledge of building requirements, board processes, and market dynamics."
              },
              {
                icon: Users,
                title: "Owner-Focused Approach",
                description: "Unlike building managing agents, we represent your interests as the unit owner — ensuring full compliance and effective management."
              },
              {
                icon: Clock,
                title: "End-to-End Service",
                description: "From acquisition analysis to leasing, management, reporting, and eventual sale — we provide a complete service offering for condo owners."
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#0066FF]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="w-8 h-8 text-[#0066FF]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A2E] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-[#1A1A2E]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Optimize Your Condo Management?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            For property owners seeking a sophisticated alternative to traditional real estate management, our Condo Unit Management service represents a proven, scalable, and highly refined solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-primary px-8 py-6 text-base rounded-lg group font-semibold">
                <Phone className="mr-2 w-5 h-5" />
                Contact Us Today
              </Button>
            </Link>
            <Link to="/properties">
              <Button variant="outline" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 px-8 py-6 text-base rounded-lg font-semibold">
                Browse Replacement Properties
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
